/**
 * SettleSheet — iter121.
 *
 * Cross-group "all my debts" bottom sheet. Opened from Home "To Pay" /
 * "To Receive" cards (and the global FAB) to give users one place to settle
 * everything they owe / are owed without drilling into individual groups.
 *
 * Each row shows: other-user name, group name, amount. Action buttons are
 * context-aware:
 *   - When I OWE someone → "Scan their QR" (opens QrScanSheet) OR "Mark paid"
 *     (opens existing manual settle flow via direct POST to qr-settle).
 *   - When I'M OWED → "Show QR" (opens QrSettleSheet with amount baked in).
 */
import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDownLeft, Camera, QrCode, Check,
  Wallet, Sparkles, RefreshCw,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from './ui/tabs';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import EmptyState from './EmptyState';
import { AllSettled } from './illustrations';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatNXC } from '../lib/formatNXC';
import QrSettleSheet from './QrSettleSheet';
import QrScanSheet from './QrScanSheet';

const INITIAL_TAB = 'owe';

export default function SettleSheet({ open, onOpenChange, defaultTab = INITIAL_TAB }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [showQr, setShowQr] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const fetchDebts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/my-debts');
      setData(res.data);
    } catch {
      toast.error('Failed to load debts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) { setTab(defaultTab); fetchDebts(); }
  }, [open, defaultTab, fetchDebts]);

  const handleMarkPaid = async (row) => {
    const key = `${row.group_id}:${row.other_id}`;
    setBusyKey(key);
    try {
      await api.post('/coin-transfers/qr-settle', {
        group_id: row.group_id,
        to_user_id: row.other_id,
        amount: row.amount,
        note: 'Marked paid from Settle sheet',
      });
      toast.success(`Settled ${formatNXC(row.amount)} with ${row.other_name}`);
      fetchDebts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to settle');
    } finally { setBusyKey(null); }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[88vh] overflow-y-auto" data-testid="settle-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Settle up
            </SheetTitle>
            <SheetDescription>
              Every debt across all your groups — settle in one tap.
            </SheetDescription>
          </SheetHeader>

          <Tabs value={tab} onValueChange={setTab} className="mt-3">
            <TabsList className="grid w-full grid-cols-2 rounded-full" data-testid="settle-sheet-tabs">
              <TabsTrigger value="owe" data-testid="tab-owe" className="rounded-full">
                <ArrowUpRight className="w-3.5 h-3.5 mr-1" />
                I owe
                {data?.owe_total > 0 && (
                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-700 dark:text-rose-300 font-bold">
                    {formatNXC(data.owe_total)}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="owed" data-testid="tab-owed" className="rounded-full">
                <ArrowDownLeft className="w-3.5 h-3.5 mr-1" />
                Owed to me
                {data?.owed_total > 0 && (
                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold">
                    {formatNXC(data.owed_total)}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="owe" className="mt-4 space-y-2" data-testid="owe-list">
              {loading && <SettleSkeleton />}
              {!loading && (data?.owe || []).length === 0 && (
                <EmptyState
                  illustration={AllSettled}
                  title="You're square!"
                  description="No outstanding debts in any group."
                  tone="success"
                  testId="owe-empty"
                />
              )}
              {!loading && (data?.owe || []).map((row) => {
                const key = `${row.group_id}:${row.other_id}`;
                return (
                  <DebtRow
                    key={key}
                    row={row}
                    tone="owe"
                    busy={busyKey === key}
                    onMarkPaid={() => handleMarkPaid(row)}
                    onScan={() => setScanOpen(true)}
                  />
                );
              })}
              {(data?.owe || []).length > 0 && (
                <Button
                  onClick={() => setScanOpen(true)}
                  className="w-full rounded-full mt-3"
                  data-testid="settle-sheet-scan-cta"
                >
                  <Camera className="w-4 h-4 mr-1.5" /> Scan their QR
                </Button>
              )}
            </TabsContent>

            <TabsContent value="owed" className="mt-4 space-y-2" data-testid="owed-list">
              {loading && <SettleSkeleton />}
              {!loading && (data?.owed || []).length === 0 && (
                <EmptyState
                  illustration={AllSettled}
                  title="Nothing owed to you"
                  description="No-one currently owes you across your groups."
                  tone="success"
                  testId="owed-empty"
                />
              )}
              {!loading && (data?.owed || []).map((row) => {
                const key = `${row.group_id}:${row.other_id}`;
                return (
                  <DebtRow
                    key={key}
                    row={row}
                    tone="owed"
                    busy={false}
                    onShowQr={() => setShowQr({
                      groupId: row.group_id,
                      groupName: row.group_name,
                      toUserId: user.id,
                      toName: user.name,
                      amount: row.amount,
                      note: `Settle with ${row.other_name}`,
                    })}
                  />
                );
              })}
            </TabsContent>
          </Tabs>

          {!loading && (
            <button
              type="button"
              onClick={fetchDebts}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-4"
              data-testid="settle-sheet-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          )}
        </SheetContent>
      </Sheet>

      {/* nested sheets */}
      <QrSettleSheet
        open={!!showQr}
        onOpenChange={(o) => !o && setShowQr(null)}
        groupId={showQr?.groupId}
        groupName={showQr?.groupName}
        toUserId={showQr?.toUserId}
        toName={showQr?.toName}
        amount={showQr?.amount}
        note={showQr?.note}
      />
      <QrScanSheet
        open={scanOpen}
        onOpenChange={setScanOpen}
        onSettled={fetchDebts}
      />
    </>
  );
}

/* ------------------- helpers ------------------- */
function DebtRow({ row, tone, busy, onMarkPaid, onShowQr, onScan }) {
  const isOwe = tone === 'owe';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      data-testid={`debt-row-${row.group_id}-${row.other_id}`}
    >
      <Card className={`p-3 rounded-2xl border ${isOwe ? 'border-rose-500/30' : 'border-emerald-500/30'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="font-semibold truncate">{row.other_name}</p>
            <p className="text-xs text-muted-foreground truncate">{row.group_name}</p>
          </div>
          <p className={`font-display font-bold text-lg tabular-nums shrink-0 ${
            isOwe ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'
          }`}>
            {formatNXC(row.amount)}
          </p>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {isOwe ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-xs flex-1"
                onClick={onScan}
                data-testid={`row-scan-${row.group_id}-${row.other_id}`}
              >
                <Camera className="w-3.5 h-3.5 mr-1" /> Scan their QR
              </Button>
              <Button
                size="sm"
                className="rounded-full text-xs flex-1"
                onClick={onMarkPaid}
                disabled={busy}
                data-testid={`row-mark-paid-${row.group_id}-${row.other_id}`}
              >
                {busy ? <Sparkles className="w-3.5 h-3.5 animate-pulse" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {busy ? 'Settling…' : 'Mark paid'}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              className="rounded-full text-xs flex-1"
              onClick={onShowQr}
              data-testid={`row-show-qr-${row.group_id}-${row.other_id}`}
            >
              <QrCode className="w-3.5 h-3.5 mr-1" /> Show QR to {row.other_name.split(' ')[0]}
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

function SettleSkeleton() {
  return (
    <div className="space-y-2" data-testid="settle-skeleton">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-2xl" />
      ))}
    </div>
  );
}
